import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientDashboardService } from '../../services/clientDashboard';
import './ClientLogin.css';

interface Transportadora {
  empresa_id: number;
  empresa_nome: string;
  empresa_razao_social: string;
  logomarca_url: string | null;
  logomarca_base64: string | null;
  cliente_razao_social: string;
}

const ClientLogin: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Estado para seleção de transportadora
  const [transportadoras, setTransportadoras] = useState<Transportadora[]>([]);
  const [showSelection, setShowSelection] = useState(false);
  const [selectingId, setSelectingId] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await clientDashboardService.login(email, senha);

      if (response.requires_selection) {
        // Múltiplas transportadoras - exibir tela de seleção
        setTransportadoras(response.transportadoras);
        setShowSelection(true);
        setLoading(false);
        return;
      }

      if (response.success) {
        navigate('/cliente/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao realizar login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTransportadora = async (empresaId: number) => {
    setError('');
    setSelectingId(empresaId);

    try {
      const response = await clientDashboardService.loginWithCarrier(email, senha, empresaId);
      if (response.success) {
        navigate('/cliente/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao selecionar transportadora.');
    } finally {
      setSelectingId(null);
    }
  };

  const handleBackToLogin = () => {
    setShowSelection(false);
    setTransportadoras([]);
    setSenha('');
    setError('');
  };

  const getLogoSrc = (t: Transportadora): string | null => {
    if (t.logomarca_base64) return t.logomarca_base64;
    if (t.logomarca_url) return t.logomarca_url;
    return null;
  };

  const togglePasswordVisibility = () => {
    const passwordField = document.getElementById('clientPassword') as HTMLInputElement;
    const toggleIcon = document.querySelector('.client-login-page .password-toggle');

    if (passwordField && toggleIcon) {
      if (passwordField.type === 'password') {
        passwordField.type = 'text';
        if (toggleIcon instanceof HTMLElement) {
          toggleIcon.textContent = '\uD83D\uDE48';
        }
      } else {
        passwordField.type = 'password';
        if (toggleIcon instanceof HTMLElement) {
          toggleIcon.textContent = '\uD83D\uDC41\uFE0F';
        }
      }
    }
  };

  // Tela de seleção de transportadora
  if (showSelection) {
    return (
      <div className="client-login-page">
        <div className="client-login-container client-login-selection-container">
          <div className="client-login-logo-section">
            <img
              className="client-login-logo"
              src="/images/logo-confereai.png"
              alt="Logo ConfereAí"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
            <p className="client-login-logo-subtitle">Selecione a transportadora</p>
          </div>

          <p className="client-login-selection-info">
            Seu acesso está vinculado a mais de uma transportadora. Escolha qual deseja acessar:
          </p>

          {error && (
            <div className="client-login-error-message">{error}</div>
          )}

          <div className="client-login-carrier-list">
            {transportadoras.map((t) => {
              const logoSrc = getLogoSrc(t);
              return (
                <button
                  key={t.empresa_id}
                  className={`client-login-carrier-card ${selectingId === t.empresa_id ? 'selecting' : ''}`}
                  onClick={() => handleSelectTransportadora(t.empresa_id)}
                  disabled={selectingId !== null}
                >
                  <div className="client-login-carrier-logo-wrapper">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={t.empresa_nome}
                        className="client-login-carrier-logo"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const fallback = parent.querySelector('.client-login-carrier-logo-fallback') as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div
                      className="client-login-carrier-logo-fallback"
                      style={{ display: logoSrc ? 'none' : 'flex' }}
                    >
                      {(t.empresa_nome || t.empresa_razao_social || '?').charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="client-login-carrier-info">
                    <span className="client-login-carrier-name">{t.empresa_nome || t.empresa_razao_social}</span>
                    {t.empresa_razao_social && t.empresa_razao_social !== t.empresa_nome && (
                      <span className="client-login-carrier-razao">{t.empresa_razao_social}</span>
                    )}
                    <span className="client-login-carrier-cliente">Cliente: {t.cliente_razao_social}</span>
                  </div>
                  <div className="client-login-carrier-arrow">
                    {selectingId === t.empresa_id ? (
                      <span className="client-login-carrier-spinner"></span>
                    ) : (
                      <span>&#8250;</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <button
            className="client-login-back-btn"
            onClick={handleBackToLogin}
            disabled={selectingId !== null}
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="client-login-page">
      <div className="client-login-container">
        <div className="client-login-logo-section">
          <img
            className="client-login-logo"
            src="/images/logo-confereai.png"
            alt="Logo ConfereAí"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          <p className="client-login-logo-subtitle">Dashboard de Performance - Acesso do Cliente</p>
        </div>

        {error && (
          <div className="client-login-error-message">{error}</div>
        )}

        <form id="clientLoginForm" onSubmit={handleSubmit}>
          <div className="client-login-form-group">
            <label htmlFor="clientEmail">E-mail</label>
            <div className="client-login-input-wrapper">
              <input
                type="email"
                id="clientEmail"
                placeholder="Digite seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
              <span className="client-login-input-icon">{'\uD83D\uDCE7'}</span>
            </div>
          </div>

          <div className="client-login-form-group">
            <label htmlFor="clientPassword">Senha</label>
            <div className="client-login-input-wrapper">
              <input
                type="password"
                id="clientPassword"
                placeholder="Digite sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                autoComplete="current-password"
              />
              <span
                className="client-login-input-icon password-toggle"
                onClick={togglePasswordVisibility}
                style={{ cursor: 'pointer' }}
              >
                {'\uD83D\uDC41\uFE0F'}
              </span>
            </div>
          </div>

          <button
            type="submit"
            className="client-login-btn"
            disabled={loading}
          >
            <span>{loading ? 'Entrando...' : 'Entrar no Dashboard'}</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ClientLogin;
